import { useState } from 'react';
import { LineChart as LineChartIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.tsx';
import { Button } from './ui/button.tsx';
import { MetricHistoryModal } from './MetricHistoryModal.tsx';
import { type MetricsYDomainMode } from './MetricsChart.tsx';
import { Sparkline } from './Sparkline.tsx';
import { type MetricTimeRange, useMetricHistory } from '../hooks/useMetricHistory.ts';
import { formatAsOfLabel } from '../lib/asOfDate.ts';

type Granularity = 'hourly' | 'daily' | 'weekly';

interface MetricCardProps {
  title: string;
  value: number | string;
  icon?: React.ReactNode;
  isLoading?: boolean;
  description?: string;
  timestamp?: string | null;
  valueClassName?: string;
  metricId?: string;
  granularity?: Granularity;
  showHistory?: boolean;
  /** Forwarded to the history modal's chart. Default 'tight'. */
  yDomainMode?: MetricsYDomainMode;
  /**
   * Sub-value breakdown shown under the main value; undefined values are
   * hidden. `note` is rendered muted after the value (e.g. "not included in
   * total users").
   */
  breakdown?: { label: string; value: number | undefined; note?: string }[];
  /**
   * Formats numeric values for display (e.g. currency). Keep `value` numeric
   * and format here — passing a pre-formatted string as `value` suppresses
   * the delta chip. Default: toLocaleString.
   */
  valueFormatter?: (value: number) => string;
  /**
   * Reference values drawn as dashed lines on the history modal's chart.
   * Must be on the same basis as the charted series — pair a monthly
   * baseline/target with `rollingSumDays` so the chart is monthly too.
   */
  baseline?: number;
  target?: number;
  /**
   * Chart the trailing N-day sum in the history modal instead of raw dailies
   * (the inline sparkline stays on the raw daily series).
   */
  rollingSumDays?: number;
  /**
   * Invert the delta chip's colors for metrics where a decrease is good news
   * (e.g. spend): down renders green, up renders red.
   */
  lowerIsBetter?: boolean;
  /**
   * Point-in-time view (YYYY-MM-DD, UTC day). Ends the sparkline/delta/hi-lo
   * window at that day and labels the footer with the period date instead of
   * the compute time (compute runs the day after the period — a day-after
   * label on a KPI slide would read as the wrong date). The history modal is
   * deliberately unaffected.
   */
  asOf?: string;
  /**
   * Sparkline series supplied by the caller (oldest first) for values that
   * are not one metric's history, e.g. a sum of two metrics or water-saved
   * months. Replaces the history fetch; the delta chip and hi/lo use it too.
   */
  sparkValues?: number[];
  /**
   * Show the delta chip. Default true. Turn off for cumulative series (e.g.
   * a year-to-date running total), whose first-to-last change only ever
   * rises and so reads as growth rather than accumulation.
   */
  showDelta?: boolean;
}

const SPARKLINE_RANGE: Record<Granularity, MetricTimeRange> = {
  hourly: '24h',
  daily: '30d',
  weekly: '90d',
};

interface Delta {
  label: string;
  direction: 'up' | 'down' | 'flat';
  aria: string;
}

function formatPct(from: number, to: number): Omit<Delta, 'aria'> | null {
  const pct = ((to - from) / Math.abs(from)) * 100;
  if (!Number.isFinite(pct)) return null;
  if (Math.abs(pct) < 0.1) {
    return { label: '≈ flat', direction: 'flat' };
  }
  const direction = pct > 0 ? 'up' : 'down';
  const sign = pct > 0 ? '+' : '';
  return { label: `${sign}${pct.toFixed(1)}%`, direction };
}

function formatDelta(values: number[]): Delta | null {
  if (values.length < 2) return null;
  const first = values[0];
  const last = values[values.length - 1];
  if (first === 0) {
    if (last === 0) return null;
    return { label: '+new', direction: 'up', aria: 'change vs first point: +new' };
  }
  const pct = formatPct(first, last);
  return pct && { ...pct, aria: `change vs first point: ${pct.label}` };
}

// When a card declares a baseline, the chip measures the headline against it —
// the same anchor as the dashed reference line — instead of trending two
// arbitrary sparkline endpoints. The two must not tell different stories.
function formatDeltaVsBaseline(
  value: number,
  baseline: number,
  formatValue: (value: number) => string,
): Delta | null {
  if (baseline === 0) return null;
  const pct = formatPct(baseline, value);
  const anchor = `vs baseline ${formatValue(baseline)}`;
  return pct && {
    ...pct,
    label: `${pct.label} ${anchor}`,
    aria: `change ${anchor}: ${pct.label}`,
  };
}

export function MetricCard({
  title,
  value,
  icon,
  isLoading = false,
  description,
  timestamp,
  valueClassName,
  metricId,
  granularity,
  showHistory = true,
  yDomainMode,
  breakdown,
  valueFormatter,
  baseline,
  target,
  rollingSumDays,
  lowerIsBetter = false,
  asOf,
  sparkValues: suppliedSparkValues,
  showDelta = true,
}: MetricCardProps) {
  const formatNumber = valueFormatter ?? ((v: number) => v.toLocaleString());
  const breakdownEntries = (breakdown ?? []).filter(
    (b): b is { label: string; value: number; note?: string } => b.value !== undefined,
  );
  const historyEnabled = Boolean(metricId && granularity && showHistory);
  const [modalOpen, setModalOpen] = useState(false);
  const historyAriaLabel = `View ${title} history`;

  const { data: historyData } = useMetricHistory({
    metricId: metricId ?? '',
    timeRange: granularity ? SPARKLINE_RANGE[granularity] : '30d',
    enabled: historyEnabled && !isLoading && !suppliedSparkValues,
    asOf,
  });

  const sparkValues = suppliedSparkValues ?? historyData?.dataPoints.map((p) => p.value) ?? [];
  const sparkMin = sparkValues.length ? Math.min(...sparkValues) : null;
  const sparkMax = sparkValues.length ? Math.max(...sparkValues) : null;
  const delta = !showDelta
    ? null
    : baseline !== undefined && typeof value === 'number'
    ? formatDeltaVsBaseline(value, baseline, formatNumber)
    : formatDelta(sparkValues);
  const showSparkline = !isLoading && (historyEnabled || !!suppliedSparkValues) &&
    sparkValues.length >= 2;

  const sparklineAria = showSparkline && sparkMin != null && sparkMax != null
    ? `${title} trend: ${formatNumber(sparkMin)} to ${
      formatNumber(sparkMax)
    } over ${sparkValues.length} points`
    : '';

  return (
    <>
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0 pb-2'>
          <CardTitle className='text-sm font-medium'>{title}</CardTitle>
          <div className='flex items-center gap-1'>
            {icon && <div className='h-4 w-4 text-muted-foreground'>{icon}</div>}
            {historyEnabled && (
              <Button
                variant='ghost'
                size='icon'
                className='h-6 w-6'
                aria-label={historyAriaLabel}
                onClick={() => setModalOpen(true)}
              >
                <LineChartIcon className='h-3.5 w-3.5' />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading
            ? (
              <div className='space-y-2'>
                <div className='h-8 w-20 bg-muted animate-pulse rounded' />
                {description && <div className='h-4 w-full bg-muted animate-pulse rounded' />}
              </div>
            )
            : (
              <>
                <div className='flex items-baseline justify-between gap-3'>
                  <div className={`text-2xl font-bold ${valueClassName ?? ''}`}>
                    {typeof value === 'number' ? formatNumber(value) : value}
                  </div>
                  {delta && typeof value === 'number' && (
                    <span
                      className={`text-xs font-medium tabular-nums ${
                        delta.direction === 'flat'
                          ? 'text-muted-foreground'
                          : (delta.direction === 'up') !== lowerIsBetter
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                      aria-label={delta.aria}
                    >
                      {delta.label}
                    </span>
                  )}
                </div>
                {breakdownEntries.length > 0 && (
                  <p className='text-xs text-muted-foreground mt-1 tabular-nums'>
                    {breakdownEntries.map((b, i) => (
                      <span key={b.label}>
                        {i > 0 && <span className='mx-1.5 text-muted-foreground/40'>·</span>}
                        {b.label}{' '}
                        <span className='font-medium'>
                          {valueFormatter ? valueFormatter(b.value) : b.value.toLocaleString()}
                        </span>
                        {b.note && (
                          <span className='ml-1 text-muted-foreground/70'>({b.note})</span>
                        )}
                      </span>
                    ))}
                  </p>
                )}
                {description && <p className='text-xs text-muted-foreground mt-1'>{description}</p>}
                {showSparkline && sparkMin != null && sparkMax != null && (
                  <div className='mt-2 flex items-end justify-between gap-3 text-muted-foreground'>
                    <Sparkline
                      values={sparkValues}
                      ariaLabel={sparklineAria}
                      width={120}
                      height={24}
                      className='flex-1'
                    />
                    <div className='text-[10px] leading-tight font-mono text-right whitespace-nowrap'>
                      <div>hi {formatNumber(sparkMax)}</div>
                      <div>lo {formatNumber(sparkMin)}</div>
                    </div>
                  </div>
                )}
                {asOf
                  ? (
                    <p className='text-xs text-muted-foreground/60 mt-2'>
                      As of {formatAsOfLabel(asOf)}
                    </p>
                  )
                  : timestamp && (
                    <p className='text-xs text-muted-foreground/60 mt-2'>
                      Queried {new Date(timestamp).toLocaleString()}
                    </p>
                  )}
              </>
            )}
        </CardContent>
      </Card>
      {historyEnabled && metricId && granularity && (
        <MetricHistoryModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          metricId={metricId}
          title={title}
          granularity={granularity}
          yDomainMode={yDomainMode}
          rollingSumDays={rollingSumDays}
          baseline={baseline}
          target={target}
          valueFormatter={valueFormatter}
        />
      )}
    </>
  );
}
